import { inject, Injectable } from '@angular/core';
import { AFConstants, AppsFlyer } from 'appsflyer-capacitor-plugin';
import { Observable, of } from 'rxjs';
import type { AFInit, OnDeepLink } from 'appsflyer-capacitor-plugin/src/appsflyer_interfaces';
import { PLATFORM_TOKEN, PlatformProvider } from '../tokens';
import { Router } from '@angular/router';
import { AppsFlyerOneLinkParams } from '../interfaces';
import { ENV_CONFIG, EnvConfig } from '../env';
import { Log, Logger } from '../shared-services';

@Injectable({ providedIn: 'root' })
export class AppsFlyerService {
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly logger: Logger = inject(Logger);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly router: Router = inject(Router);

  initializeDeepLinking(): void {
    if (!this.platformProvider.isNativeRun || !this.env.production) {
      this.logger.log(Log.Startup, { message: 'AppsFlyerDeepLinkingStep SKIPPED' });
      return;
    }

    try {
      AppsFlyer.addListener(AFConstants.UDL_CALLBACK, (result: OnDeepLink) => {
        if (result.status === 'FOUND' && result.deepLink) {
          const deepLinkValue: string = result.deepLink.deep_link_value || result.deepLink.deepLinkValue;
          if (deepLinkValue) {
            this.logger.log(Log.Startup, { message: `AppsFlyer navigating to: ${deepLinkValue}` });
            this.router.navigateByUrl(deepLinkValue);
          }
        }
      });

      const config: AFInit = {
        devKey: this.env.appsFlyerDevKey,
        appID: this.env.appId || '',
        isDebug: false,
        registerOnDeepLink: true,
        waitForATTUserAuthorization: 10
      };

      AppsFlyer.initSDK(config).catch((error: Error) => {
        console.log(error);
      });
    } catch (error) {
      console.log(error);
    }
  }

  logEvent(eventName: string, eventValue: Record<string, unknown> = {}): void {
    if (!this.platformProvider.isNativeRun || !this.env.production) {
      return;
    }

    AppsFlyer.logEvent({ eventName, eventValue }).catch((error: Error) => {
      console.log(error);
    });
  }

  generateOneLink(params: AppsFlyerOneLinkParams): Observable<string> {
    if (!this.env.production) {
      throw new Error('[AppsFlyerService] AppsFlyer is only available in production');
    }

    const baseUrl: string = `${this.env.appsFlyerOneLinkDomain}/${this.env.appsFlyerOneLinkTemplateId}`;

    const queryParamsObj: Record<string, string> = {
      deep_link_value: params.deepLinkValue,
      af_force_deeplink: 'true'
    };

    if (params.pid) queryParamsObj['pid'] = params.pid;
    if (params.c) queryParamsObj['c'] = params.c;
    if (params.af_channel) queryParamsObj['af_channel'] = params.af_channel;
    if (params.customParams) {
      Object.assign(queryParamsObj, params.customParams);
    }

    const queryParams: URLSearchParams = new URLSearchParams(queryParamsObj);

    return of(`${baseUrl}?${queryParams.toString()}`);
  }
}
